<template>
  <div
    class="public-bookings-page"
    :class="{ 'public-bookings-page--embedded': embedded }"
  >
    <modal-loading
      v-if="is_loading"
      :openModal="is_loading"
      :value="100"
      label="carregando..."
      :variant="'white'"
      :animated="false"
    >
    </modal-loading>

    <div v-if="!is_loading" class="public-bookings-wrapper">
      <header
        class="public-bookings-hero"
        :class="{ 'public-bookings-hero--dated': confirmedDate }"
        v-if="!hideHeader"
      >
        <div class="sales-page-profile">
          <div class="sales-page-profile__cover"></div>
          <div class="public-bookings-container">
            <div class="sales-page-profile__card">
              <div v-if="placeLogoConfigured" class="sales-page-profile__logo">
                <img
                  v-if="placeLogoUrl"
                  :src="placeLogoUrl"
                  :alt="placeName || 'Logo'"
                />
              </div>
              <h1 class="sales-page-profile__name">
                {{ placeName || 'Seu negócio' }}
              </h1>
              <p v-if="confirmedDate && !slotDated && !showAllDated" class="sales-page-profile__date">
                Saídas confirmadas para {{ confirmedDateLabel }}
              </p>
              <p v-if="confirmedDate && slotDated" class="sales-page-profile__date">
                Saídas disponíveis para {{ confirmedDateLabel }}<template v-if="confirmedSlotTimeLabel"> · {{ confirmedSlotTimeLabel }}</template>
              </p>
            <p v-if="confirmedDate && showAllDated && !slotDated" class="sales-page-profile__date">
              Todas as saídas para {{ confirmedDateLabel }}
            </p>
            </div>
          </div>
        </div>
      </header>

      <div class="public-bookings-container public-bookings-content">
        <div class="public-bookings-search pt-4">
          <div :class="{ 'row justify-content-center': embedded }">
            <div :class="{ 'col-8': embedded }">
              <div class="public-bookings-search-field">
                <input
                  v-model="searchQuery"
                  class="search-input"
                  placeholder="pesquisar"
                />
                <button
                  v-if="searchQuery"
                  type="button"
                  class="public-bookings-search-clear"
                  title="Limpar busca"
                  @click="clearSearchQuery"
                >
                  <i class="fas fa-times"></i>
                </button>
                <i v-else class="fas fa-search text-muted"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="public-bookings-results">
        <div v-if="queriedData.length == 0" class="public-bookings-empty">
          <h2 class="public-bookings-empty-title">
            Nenhum atrativo encontrado
          </h2>
          <p class="public-bookings-empty-text">
            Tente buscar com outro termo ou volte mais tarde.
          </p>
        </div>

        <template v-else>
          <section
            v-for="(place, index) in groupBy(
              queriedData,
              (poi) => poi.saleUnit
            )"
            :key="index"
            class="public-bookings-section"
          >
            <div class="public-bookings-section-header">
              <h2 class="public-bookings-section-title">
                {{ place[0]?place[0].replace('Passeio','Atrativo'):'' || 'Outro' }}s
              </h2>
              <span class="public-bookings-section-count">
                {{ place[1].length }}
                {{ place[1].length > 1 ? 'opções' : 'opções' }}
              </span>
            </div>

            <div class="public-bookings-grid">
              <public-booking-card
                v-for="(product, idx) in place[1]"
                :key="getProductKey(product) || idx"
                :product="catalogProduct(product)"
                :poi="poiForProduct(product)"
                :available="productAvailable(product)"
                :has-action="cardHasAction(product)"
                :has-quotation="productHasQuotationSale(product)"
                :confirmed-date="confirmedDate"
                :quotation-only="quotationOnly"
                :unavailable-label="unavailableLabel"
                :default-image="defaultProductImage"
                :cover-url="getProductImage(product)"
                @open-card="openCatalogCard(product, $event)"
                @reserve="openExpressBooking(product)"
                @catalog-reserve="openCatalogBooking(product)"
                @quote="openExpressBooking(product, { paylater: true })"
              />
            </div>
          </section>
        </template>
        </div>

        <div
          v-if="queriedData.length && total > pagination.perPage"
          class="public-bookings-pagination"
        >
          <base-pagination
            :current="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
            @change="paginationChanged($event)"
          >
          </base-pagination>
        </div>
      </div>

      <footer v-if="!embedded" class="public-bookings-footer-fixed">
        <content-footer
          textAlign="center"
          backgroundColor="#fff"
        ></content-footer>
      </footer>
    </div>

    <b-modal
      v-model="expressBookingModalOpen"
      modal-class="public-express-booking-modal"
      size="xl"
      hide-footer
      :title="expressBookingTitle"
      body-class="p-0"
      no-enforce-focus
      @hidden="expressBookingUrl = ''"
    >
      <iframe
        v-if="expressBookingUrl"
        :src="expressBookingUrl"
        title="Reserva expressa"
        class="public-express-booking-frame"
      ></iframe>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import ModalLoading from '../../components/ModalLoading/ModalLoading.vue'
import usePaginateTable from '../../Composables/usePaginateTable'
import ContentFooter from '../Layout/ContentFooter.vue'
import nodeService from '@/services/nodejs/index'
import { fetchFileObjectUrl, loadPublicLogoUrl } from '@/store/utils/publicLogoFile'
import { visibilityMapFromProducts } from '@/store/utils/productVisibility'
import PublicBookingCard from './PublicBookingCard.vue'
export default {
  component: 'PublicBookings',

  setup(props, context) {
    const paginateTable = usePaginateTable(context)

    const total = paginateTable.total
    const from = paginateTable.from
    const to = paginateTable.to
    const queriedData = paginateTable.queriedData
    const pagination = paginateTable.getPagination()
    const billItems = paginateTable.getBillsItems()
    const searchQuery = paginateTable.searchQuery

    function createFuseSearch(keys) {
      return paginateTable.createFuseSearch(keys)
    }
    function setBillItems(items) {
      return paginateTable.setBillsItems(items)
    }

    return {
      total,
      from,
      to,
      queriedData,
      pagination,
      billItems,
      searchQuery,
      createFuseSearch,
      setBillItems,
    }
  },

  components: {
    ModalLoading,
    ContentFooter,
    PublicBookingCard,
  },

  props: {
    hideHeader: {
      type: Boolean,
      default: false,
    },
    placeUuid: {
      type: String,
      default: null,
    },
    bookingDate: {
      type: String,
      default: '',
    },
    showAll: {
      type: [Boolean, String],
      default: false,
    },
    slotMode: {
      type: [Boolean, String],
      default: false,
    },
    slotTime: {
      type: String,
      default: '',
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      is_loading: true,
      defaultProductImage: require('@/assets/img/j-logo.png'),
      placeLogoUrl: null,
      placeLogoObjectUrl: null,
      placeLogoConfigured: false,
      productImageUrls: {},
      productImageObjectUrls: {},
      productImageLoading: {},
      visibleProductUuids: null,
      quotationSaleUuids: null,
      expressBookingModalOpen: false,
      expressBookingUrl: '',
      expressBookingTitle: 'Cotação',
      catalogRequest: 0,
      catalogPlaceUuid: null,
      poiByUuid: {},
      productDetailsByUuid: {},
    }
  },

  watch: {
    '$route.params.booking_date'() {
      this.onBookingDateRouteChange()
    },
    '$route.params.slot'() {
      this.recreateSearchableTable()
    },
    '$route.params.time'() {
      this.recreateSearchableTable()
    },
    bookingDate() {
      this.loadCatalog()
    },
    showAll() {
      this.recreateSearchableTable()
    },
    slotMode() {
      this.recreateSearchableTable()
    },
    slotTime() {
      this.recreateSearchableTable()
    },
    publicProducts(newValue) {
      if (newValue) {
        this.recreateSearchableTable()
        this.loadProductImages(this.visibleProducts(newValue))
      }
    },
    place_public_data: {
      deep: true,
      handler() {
        this.loadPlaceLogo()
      },
    },
  },

  computed: {
    ...mapGetters('product', {
      publicProducts: 'getPublicProducts',
      isLoading: 'getIsLoading',
    }),

    ...mapGetters('auth', {
      userIsLoggedIn: 'isAuthenticated',
      user: 'user',
      place: 'getPlace',
    }),

    ...mapGetters('booking', {
      place_public_data: 'getPlacePublicData',
    }),

    placeName() {
      return this.place_public_data &&
        this.place_public_data.place &&
        this.place_public_data.place.name
        ? this.place_public_data.place.name
        : ''
    },

    quotationOnly() {
      return !this.confirmedDate
    },

    routeListSegment() {
      return this.$route && this.$route.params ? this.$route.params.slot : ''
    },

    showAllDated() {
      if (!this.confirmedDate) return false
      if (this.showAll === true || this.slotMode === true) return true
      if (
        this.isShowAllSegment(this.showAll) ||
        this.isSlotSegment(this.slotMode)
      ) {
        return true
      }
      return (
        this.isShowAllSegment(this.routeListSegment) ||
        this.isSlotSegment(this.routeListSegment)
      )
    },

    slotDated() {
      if (!this.confirmedDate) return false
      if (this.slotMode === true) return true
      if (this.isSlotSegment(this.slotMode)) return true
      return this.isSlotSegment(this.routeListSegment)
    },

    routeTimeSegment() {
      return this.$route && this.$route.params ? this.$route.params.time : ''
    },

    confirmedSlotTime() {
      if (!this.slotDated) return ''
      return this.parseSlotTime(this.slotTime || this.routeTimeSegment)
    },

    confirmedSlotTimeLabel() {
      if (!this.confirmedSlotTime) return ''
      const parsed = moment(this.confirmedSlotTime, 'HH:mm', true)
      if (!parsed.isValid()) return ''
      return parsed.format('mm') === '00'
        ? `${parsed.format('HH')}h`
        : parsed.format('HH[h]mm')
    },

    confirmedDate() {
      const raw = this.bookingDate
        ? this.bookingDate
        : this.$route && this.$route.params
        ? this.$route.params.booking_date
        : ''
      return this.parseBookingDate(raw)
    },

    confirmedDateLabel() {
      if (!this.confirmedDate) return ''
      return moment(this.confirmedDate, 'YYYY-MM-DD').format('DD/MM/YY')
    },

    unavailableLabel() {
      if (!this.confirmedDate) return ''
      return this.confirmedDate === moment().format('YYYY-MM-DD')
        ? 'indisponível para hoje'
        : 'indisponível para esta data'
    },
  },

  methods: {
    ...mapActions('product', ['fetchPublicProducts', 'fetchPOIs', 'setIsLoading']),

    ...mapActions('booking', ['fetchPlacePublicData']),

    clearSearchQuery() {
      this.searchQuery = ''
    },

    clearPlaceLogoObjectUrl() {
      if (this.placeLogoObjectUrl) {
        URL.revokeObjectURL(this.placeLogoObjectUrl)
        this.placeLogoObjectUrl = null
      }
    },

    async loadPlaceLogo() {
      this.clearPlaceLogoObjectUrl()
      this.placeLogoUrl = null
      this.placeLogoConfigured = false

      try {
        const loaded = await loadPublicLogoUrl(this.place_public_data)
        this.placeLogoConfigured = !!loaded.configured
        if (!loaded.url) return
        if (loaded.owned) this.placeLogoObjectUrl = loaded.url
        this.placeLogoUrl = loaded.url
      } catch (error) {
        console.warn('Não foi possível carregar o logotipo do gestor:', error)
      }
    },

    getProductKey(product) {
      if (!product) return null
      return product.uuid || product.product_uuid || product.id || null
    },

    resolveProductImageSource(product) {
      if (!product) return ''

      let source = product.cover
      if (!source && product.images && product.images.length > 0) {
        source = product.images[0]
      }
      if (!source) {
        source = this.resolveXFieldValue(product, 'cover')
      }
      if (!source) {
        source = product.img
      }

      if (Array.isArray(source)) {
        source = source[0]
      }
      if (source && typeof source === 'object') {
        source =
          source.url ||
          source.src ||
          source.filepath ||
          source.value ||
          source.label ||
          ''
      }
      if (typeof source !== 'string' || !source.trim()) return ''

      if (source.indexOf('google.com') > 0) {
        const match = /d\/([^/]+)\/view/.exec(source)
        if (match && match[1]) {
          return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w400`
        }
      }

      return source.trim()
    },

    isDirectMediaUrl(value) {
      if (!value || typeof value !== 'string') return false
      return (
        value.indexOf('http://') === 0 ||
        value.indexOf('https://') === 0 ||
        value.indexOf('blob:') === 0 ||
        value.indexOf('data:image') === 0
      )
    },

    getProductImage(product) {
      const key = this.getProductKey(product)
      if (key && this.productImageUrls[key]) {
        return this.productImageUrls[key]
      }

      const source = this.resolveProductImageSource(product)
      if (!source) return null

      if (this.isDirectMediaUrl(source)) {
        return source
      }

      return this.defaultProductImage
    },

    async loadProductImage(product) {
      const key = this.getProductKey(product)
      if (!key || this.productImageLoading[key] || this.productImageUrls[key]) {
        return
      }

      const source = this.resolveProductImageSource(product)
      if (!source) return

      if (this.isDirectMediaUrl(source)) {
        this.$set(this.productImageUrls, key, source)
        return
      }

      this.$set(this.productImageLoading, key, true)
      try {
        const objectUrl = await fetchFileObjectUrl(source)
        if (!objectUrl) return
        if (this.productImageObjectUrls[key]) {
          URL.revokeObjectURL(this.productImageObjectUrls[key])
        }
        this.$set(this.productImageObjectUrls, key, objectUrl)
        this.$set(this.productImageUrls, key, objectUrl)
      } catch (error) {
        console.warn(
          'Não foi possível carregar a imagem do atrativo:',
          key,
          error
        )
      } finally {
        this.$set(this.productImageLoading, key, false)
      }
    },

    loadProductImages(products) {
      ;(products || []).forEach((product) => {
        this.loadProductImage(product)
      })
    },

    clearProductImageUrls() {
      Object.keys(this.productImageObjectUrls || {}).forEach((key) => {
        URL.revokeObjectURL(this.productImageObjectUrls[key])
      })
      this.productImageObjectUrls = {}
      this.productImageUrls = {}
      this.productImageLoading = {}
    },

    resolveXFieldValue(product, key) {
      if (!product || !key) return null

      const unwrap = (raw) => {
        if (raw == null) return null
        if (typeof raw === 'object' && raw.value !== undefined) return raw.value
        return raw
      }

      if (product[key] != null && product[key] !== '') {
        return unwrap(product[key])
      }

      let xfields = product.xfields
      if (typeof xfields === 'string') {
        try {
          xfields = JSON.parse(xfields)
        } catch (error) {
          return null
        }
      }
      if (!xfields) return null

      if (xfields[key] != null) {
        return unwrap(xfields[key])
      }

      const laravel = xfields.laravel_data
      if (laravel && typeof laravel.value === 'string') {
        try {
          const parsed = JSON.parse(laravel.value)
          if (parsed && parsed[key] != null) return unwrap(parsed[key])
        } catch (error) {
          /* laravel_data.value is not JSON */
        }
      }
      if (laravel && laravel[key] != null) {
        return unwrap(laravel[key])
      }

      return null
    },

    async loadVisibleProductUuids(placeUuid) {
      if (!placeUuid) return

      try {
        const res = await nodeService.getProductsByPlace(placeUuid)
        this.visibleProductUuids = visibilityMapFromProducts(res.data)
        const quotationMap = {}
        const details = {}
        ;(res.data || []).forEach((product) => {
          const key = this.getProductKey(product)
          if (!key) return
          const uuid = String(key).toUpperCase()
          details[uuid] = product
          quotationMap[uuid] =
            this.resolveXFieldValue(product, 'quotationsale') == true
        })
        this.productDetailsByUuid = details
        this.quotationSaleUuids = quotationMap
      } catch (error) {
        console.warn(
          'Não foi possível carregar a visibilidade dos atrativos:',
          error
        )
        this.visibleProductUuids = null
        this.quotationSaleUuids = null
        this.productDetailsByUuid = {}
      }
    },

    isListedProduct(product) {
      const own = this.resolveXFieldValue(product, 'visibility')
      if (own !== null && own !== '') {
        return this.isXFieldEnabled(product, 'visibility')
      }
      if (!this.visibleProductUuids) return true
      const key = this.getProductKey(product)
      if (!key) return false
      return !!this.visibleProductUuids[String(key).toUpperCase()]
    },

    visibleProducts(products) {
      return (products || []).filter((product) => {
        if (!this.isListedProduct(product)) return false
        if (
          this.confirmedDate &&
          !this.showAllDated &&
          !this.productAvailable(product)
        )
          return false
        return true
      })
    },

    isXFieldEnabled(product, keys) {
      const candidates = Array.isArray(keys) ? keys : [keys]
      return candidates.some((key) => {
        const value = this.resolveXFieldValue(product, key)
        if (value === true || value === 1 || value === '1') return true
        if (typeof value === 'string') {
          const normalized = value.trim().toLowerCase()
          return (
            normalized === 'true' ||
            normalized === 'yes' ||
            normalized === 'on'
          )
        }
        return false
      })
    },

    isOwnPlaceProduct(product) {
      if (!this.userIsLoggedIn || !product) return false
      const productPlace =
        product.placeUUID || product.placeUuid || product.place_uuid || ''
      const userPlace =
        (this.user && (this.user.placeUuid || this.user.place_uuid)) ||
        (this.place && (this.place.uuid || this.place.place_uuid)) ||
        ''
      if (!productPlace || !userPlace) return false
      return (
        String(productPlace).toUpperCase() === String(userPlace).toUpperCase()
      )
    },

    onlineSaleLabel(product) {
      return this.isOwnPlaceProduct(product)
        ? 'Criar reserva'
        : 'Reservar Online'
    },

    quotationSaleLabel(product) {
      return this.isOwnPlaceProduct(product)
        ? 'Criar cotação'
        : 'Fazer cotação'
    },

    canOnlineSale(product) {
      if (this.isXFieldEnabled(product, ['onlinesale', 'online_sale'])) {
        return true
      }
      return (
        this.userIsLoggedIn &&
        this.isXFieldEnabled(product, ['localsale', 'local_sale'])
      )
    },

    canQuotationSale(product) {
      return this.isXFieldEnabled(product, [
        'quotationsale',
        'quotationsalex',
        'quotation_sale',
      ])
    },

    productHasQuotationSale(product) {
      if (!product) return false
      if (this.resolveXFieldValue(product, 'quotationsale') == true) return true
      const key = this.getProductKey(product)
      if (!key || !this.quotationSaleUuids) return false
      return this.quotationSaleUuids[String(key).toUpperCase()] === true
    },

    hasBookingAction(product) {
      return this.canOnlineSale(product) || this.canQuotationSale(product)
    },

    cardHasAction(product) {
      if (this.quotationOnly) return true
      if (this.confirmedDate) return this.productAvailable(product)
      return this.hasBookingAction(product)
    },

    parseBookingDate(value) {
      if (!value || String(value).toLowerCase() === 'today') return ''
      const parsed = moment(String(value), 'YYYY-MM-DD', true)
      return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
    },

    isShowAllSegment(value) {
      return String(value || '').toLowerCase() === 'showall'
    },

    isSlotSegment(value) {
      return String(value || '').toLowerCase() === 'slot'
    },

    parseSlotTime(value) {
      if (!value) return ''
      const parsed = moment(
        String(value).trim(),
        ['HH:mm', 'H:mm', 'HH-mm', 'H-mm', 'HH[h]mm', 'H[h]mm', 'HH[h]', 'H[h]'],
        true
      )
      return parsed.isValid() ? parsed.format('HH:mm') : ''
    },

    parseCapacity(poi) {
      const raw = poi && poi.capacity
      if (!raw) return null
      if (typeof raw === 'string') {
        try {
          return JSON.parse(raw)
        } catch (error) {
          return null
        }
      }
      return raw
    },

    poiHasCapacityOnDate(poi, date) {
      const capacity = this.parseCapacity(poi)
      if (!capacity || !date) return false
      const months = Object.entries(capacity).filter((entry) =>
        moment(entry[0]).isValid()
      )
      return months.some((entry) => {
        const slots = entry[1]
        if (!slots || typeof slots !== 'object') return false
        return Object.entries(slots).some((slot) => {
          const day = String(slot[0]).split(' ')[0]
          if (day !== date) return false
          const info = slot[1] || {}
          return Number(info.available) > 0
        })
      })
    },

    parseAvailability(poi) {
      const raw = poi && poi.availability
      if (!raw) return null
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw)
          return parsed && typeof parsed === 'object' ? parsed : null
        } catch (error) {
          return null
        }
      }
      return typeof raw === 'object' ? raw : null
    },

    poiHasAvailabilityOnDate(poi, date) {
      const availability = this.parseAvailability(poi)
      if (!availability || !date) return false
      const dateMoment = moment(date, 'YYYY-MM-DD', true)
      if (!dateMoment.isValid()) return false
      const weekday = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ][dateMoment.day()]
      return Object.entries(availability).some((entry) => {
        const blocks = entry[1]
        if (!Array.isArray(blocks) || !blocks.length) return false
        const keyDate = moment(entry[0], 'YYYY-MM-DD', true)
        if (keyDate.isValid() && keyDate.format('YYYY-MM-DD') === date) {
          return true
        }
        return String(entry[0]).toLowerCase() === weekday
      })
    },

    poiHasAvailabilityAtTime(poi, date, time) {
      const availability = this.parseAvailability(poi)
      const target = this.parseSlotTime(time)
      if (!availability || !date || !target) return false
      const dateMoment = moment(date, 'YYYY-MM-DD', true)
      if (!dateMoment.isValid()) return false
      const weekday = [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ][dateMoment.day()]
      return Object.entries(availability).some((entry) => {
        const blocks = entry[1]
        if (!Array.isArray(blocks) || !blocks.length) return false
        const keyDate = moment(entry[0], 'YYYY-MM-DD', true)
        const matchesDay =
          (keyDate.isValid() && keyDate.format('YYYY-MM-DD') === date) ||
          String(entry[0]).toLowerCase() === weekday
        if (!matchesDay) return false
        return blocks.some(
          (block) => this.parseSlotTime(block && block.start) === target
        )
      })
    },

    productAvailable(product) {
      if (!this.confirmedDate) return false
      const key = this.getProductKey(product)
      if (!key) return false
      const poi = this.poiByUuid[String(key).toUpperCase()]
      if (this.slotDated) {
        if (this.confirmedSlotTime) {
          return this.poiHasAvailabilityAtTime(
            poi || product,
            this.confirmedDate,
            this.confirmedSlotTime
          )
        }
        return this.poiHasAvailabilityOnDate(poi || product, this.confirmedDate)
      }
      return this.poiHasCapacityOnDate(poi, this.confirmedDate)
    },

    poiForProduct(product) {
      const key = this.getProductKey(product)
      if (!key) return null
      return this.poiByUuid[String(key).toUpperCase()] || null
    },

    catalogProduct(product) {
      const key = this.getProductKey(product)
      const details = key
        ? this.productDetailsByUuid[String(key).toUpperCase()]
        : null
      if (!details) return product
      return Object.assign({}, details, product, {
        short_description:
          details.short_description || product.short_description,
        images:
          details.images &&
          (Array.isArray(details.images) ? details.images.length : details.images)
            ? details.images
            : product.images,
        location_city: details.location_city,
        allowed_age: details.allowed_age,
        activity_duration: details.activity_duration,
        activity_difficulty: details.activity_difficulty,
        activity_price: details.activity_price,
        xfields: details.xfields || product.xfields,
        availability: details.availability || product.availability,
      })
    },

    openCatalogCard(product, event) {
      if (!product) return
      if (this.quotationOnly || this.confirmedDate) {
        if (event && typeof event.preventDefault === 'function') {
          event.preventDefault()
        }
        return
      }
      if (!this.cardHasAction(product)) return
      this.openPrimaryBooking(product)
    },

    openPrimaryBooking(product) {
      if (!product || !this.hasBookingAction(product)) return
      if (this.canOnlineSale(product)) {
        this.openExpressBooking(product)
        return
      }
      this.openExpressBooking(product, { paylater: true })
    },

    openCatalogBooking(product) {
      if (!product) return
      this.openExpressBooking(product)
    },

    openExpressBooking(product, options = {}) {
      if (!product) return
      if (
        !this.quotationOnly &&
        this.confirmedDate &&
        !this.productAvailable(product)
      )
        return
      const landing = product.configuration
        ? product.configuration.landing_page_params || {}
        : {}
      let product_url = {
        place_uuid:
          product.placeUUID || product.place_uuid || this.catalogPlaceUuid,
        product_uuid: product.uuid,
        ...landing,
        ...options,
      }
      if (!this.quotationOnly && this.confirmedDate) {
        product_url.booking_date = this.confirmedDate
        delete product_url.quote_today
        delete product_url.auto_checkin
        if (!options.paylater) delete product_url.paylater
      }
      if (this.embedded) {
        product_url.from_modal = true
      }
      product_url = JSON.stringify(product_url)
      product_url = btoa(product_url)
      const bookingUrl = `/booking/?${product_url}`
      if (this.embedded || this.quotationOnly || this.confirmedDate) {
        this.expressBookingTitle = options.quote_today
          ? 'Cotação para hoje'
          : options.paylater
          ? 'Cotação'
          : 'Nova reserva'
        this.expressBookingUrl = bookingUrl
        this.expressBookingModalOpen = true
        return
      }
      window.open(bookingUrl, '_blank')
      this.$emit('close-public-bookings-modal')
    },

    paginationChanged(page) {
      this.pagination.currentPage = page
    },

    recreateSearchableTable() {
      const visible = this.visibleProducts(this.publicProducts)
      this.setBillItems(visible)

      this.createFuseSearch(['name', 'placeUUID', 'saleUnit', 'description'])
    },

    groupBy(list, keyGetter) {
      const map = new Map()
      list.forEach((item) => {
        const key = keyGetter(item)
        const collection = map.get(key)

        if (!collection) {
          map.set(key, [item])
        } else {
          collection.push(item)
        }
      })
      return Array.from(map)
    },

    goToSite() {
      const URL_LOJA = this.place_public_data.place.website
      window.open(URL_LOJA, '_self')
    },

    async redirectTodaySegment() {
      if (this.bookingDate) return false
      const segment =
        this.$route && this.$route.params
          ? this.$route.params.booking_date
          : ''
      if (!segment || String(segment).toLowerCase() !== 'today') return false
      const place = this.$route.params.place_uuid
      if (!place) return false
      const today = moment().format('YYYY-MM-DD')
      const listSegment =
        this.$route.params && this.$route.params.slot
          ? String(this.$route.params.slot).toLowerCase()
          : ''
      const timeSegment =
        this.$route.params && this.$route.params.time
          ? this.parseSlotTime(this.$route.params.time)
          : ''
      const suffix =
        this.isShowAllSegment(listSegment) || this.isSlotSegment(listSegment)
          ? `/${listSegment}${
              this.isSlotSegment(listSegment) && timeSegment
                ? `/${timeSegment}`
                : ''
            }`
          : ''
      await this.$router.replace(`/public-bookings/${place}/${today}${suffix}`)
      return true
    },

    async onBookingDateRouteChange() {
      const redirected = await this.redirectTodaySegment()
      if (redirected) return
      await this.loadCatalog()
    },

    async loadDateCapacity(placeUuid) {
      this.poiByUuid = {}
      if (!placeUuid) return
      try {
        const res = await nodeService.getPoisByVendor({
          PLACE_UUID: placeUuid,
          PRODUCT_UUID: '',
        })
        const list = Array.isArray(res.data)
          ? res.data
          : res.data
          ? [res.data]
          : []
        const map = {}
        list.forEach((poi) => {
          const key = poi && (poi.uuid || poi.product_uuid)
          if (!key) return
          map[String(key).toUpperCase()] = poi
        })
        this.poiByUuid = map
      } catch (error) {
        console.warn('Não foi possível carregar a vaga da data:', error)
        this.poiByUuid = {}
      }
      this.recreateSearchableTable()
    },

    async loadCatalog() {
    const seq = ++this.catalogRequest
    this.is_loading = true
    const urlParams = new URLSearchParams(window.location.search)
    const iframedPois = urlParams.get('pois')
    const iframedPlaceUuid = urlParams.get('place_uuid')

    const placeUuid = this.placeUuid
      ? this.placeUuid
      : this.$route.params.place_uuid
      ? this.$route.params.place_uuid
      : iframedPlaceUuid
      ? iframedPlaceUuid
      : null

    this.catalogPlaceUuid = placeUuid
    await this.loadVisibleProductUuids(placeUuid)
    await this.fetchPublicProducts(placeUuid)
    if (this.userIsLoggedIn && placeUuid) {
      await this.fetchPOIs({ PLACE_UUID: placeUuid, PRODUCT_UUID: '' })
    }
    if (seq !== this.catalogRequest) return
    this.loadProductImages(this.visibleProducts(this.publicProducts))
    await this.fetchPlacePublicData(placeUuid)
    await this.loadPlaceLogo()
    await this.loadDateCapacity(placeUuid)
    if (seq !== this.catalogRequest) return
    this.pagination.perPage = 100
    this.recreateSearchableTable()
    if (iframedPois && !this.confirmedDate) {
      this.openExpressBooking(
        this.publicProducts.find((product) => product.uuid === iframedPois)
      )
    } else {
      this.setIsLoading(false)
      this.is_loading = false
    }
    },

    onExpressBookingMessage(event) {
      if (event.origin !== window.location.origin) return
      if (!event.data || event.data.type !== 'close-express-booking') return
      this.expressBookingModalOpen = false
      if (this.embedded) {
        this.$emit('close-public-bookings-modal')
      }
    },
  },

  async mounted() {
    window.addEventListener('message', this.onExpressBookingMessage)
    const redirected = await this.redirectTodaySegment()
    if (redirected) return
    await this.loadCatalog()
  },

  beforeDestroy() {
    window.removeEventListener('message', this.onExpressBookingMessage)
    this.clearPlaceLogoObjectUrl()
    this.clearProductImageUrls()
  },
}
</script>

<style>
.public-bookings-page {
  min-height: 100vh;
  background-color: #f5f9f9;
}

.public-bookings-page--embedded {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: transparent;
}

.public-bookings-page--embedded .public-bookings-wrapper {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
}

.public-bookings-page--embedded .public-bookings-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
}

.public-bookings-page--embedded .public-bookings-search {
  flex: 0 0 auto;
  width: 100%;
  max-width: none;
  margin-bottom: 16px;
}

.public-bookings-search-field {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px;
  border: 0.5px solid #dadada;
  border-radius: 5px;
  background-color: #fff;
}

.public-bookings-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0.35rem;
  padding: 0;
  width: 1.35rem;
  height: 1.35rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #b11b1b;
  cursor: pointer;
  flex-shrink: 0;
}

.public-bookings-search-clear:hover {
  background-color: #f8f1ee;
  color: #b11b1b;
}

.public-bookings-page--embedded .public-bookings-results {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.public-bookings-page--embedded .public-bookings-pagination {
  flex: 0 0 auto;
  margin-top: 0;
  padding-top: 12px;
  background: #fff;
  border-top: 1px solid #e8edf0;
}

.public-bookings-page--embedded .public-bookings-grid {
  grid-template-columns: repeat(2, 1fr);
}

.public-bookings-wrapper {
  padding-bottom: 56px;
}

.public-bookings-container {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;
}

.public-bookings-hero {
  margin-bottom: 32px;
}

.public-bookings-hero .sales-page-profile {
  margin: 0;
}

.public-bookings-hero .sales-page-profile__cover {
  height: 110px;
  background-image: linear-gradient(87deg, #172b4d 0, #12576e 100%);
}

.public-bookings-hero .sales-page-profile__card {
  position: relative;
  margin: -4.5rem 0 0;
  padding: 0 1.25rem 1.5rem;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 0 2rem 0 rgba(136, 152, 170, 0.15);
  text-align: center;
}

.public-bookings-hero .sales-page-profile__logo {
  width: 7rem;
  height: 7rem;
  margin: -3.5rem auto 1rem;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #fff;
  background: #fff;
  box-shadow: 0 4px 12px rgba(20, 103, 130, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}

.public-bookings-hero .sales-page-profile__logo img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.public-bookings-hero--dated .sales-page-profile__card {
  text-align: left;
}

.public-bookings-hero--dated .sales-page-profile__logo {
  margin-left: 0;
  margin-right: 0;
}

.public-bookings-hero--dated .sales-page-profile__date {
  margin: 0.35rem 0 0;
  color: #12576e;
  font-size: 1rem;
  font-weight: 600;
}

.public-bookings-hero .sales-page-profile__name {
  margin: 0 0 0.15rem;
  color: #12576e;
  font-size: 1.35rem;
  font-weight: 700;
}

.public-bookings-hero .sales-page-profile__role {
  margin: 0 0 0.85rem;
  color: #8898aa;
  font-size: 0.9rem;
  font-weight: 500;
}

.public-bookings-hero .sales-page-profile__text {
  margin: 0 auto;
  max-width: 32rem;
  color: #525f7f;
  font-size: 0.95rem;
  line-height: 1.5;
}

.public-bookings-content {
  padding-bottom: 24px;
}

.public-bookings-search {
  max-width: 420px;
  margin: 0 auto 40px;
}

.public-bookings-empty {
  text-align: center;
  padding: 48px 16px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8edf0;
}

.public-bookings-empty-title {
  color: #1e293b;
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.public-bookings-empty-text {
  color: #64748b;
  margin: 0;
}

.public-bookings-section {
  margin-bottom: 48px;
}

.public-bookings-section-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #12576e;
}

.public-bookings-section-title {
  color: #12576e;
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
}

.public-bookings-section-count {
  color: #64748b;
  font-size: 0.95rem;
  white-space: nowrap;
}

.public-bookings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.public-bookings-grid > * {
  height: 100%;
  min-height: 0;
}

.public-bookings-pagination {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.public-bookings-footer-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 10;
  background: #fff;
  border-top: 1px solid #e8edf0;
}

.public-bookings-footer-fixed .footer {
  position: static !important;
  width: 100%;
}

@media (max-width: 992px) {
  .public-bookings-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .public-bookings-hero .sales-page-profile__name {
    font-size: 1.2rem;
  }
}

.public-express-booking-modal {
  z-index: 1080;
}

.public-express-booking-modal .modal-dialog {
  max-width: min(1200px, 96vw);
  width: 96vw;
  height: calc(100vh - 2rem);
  margin: 1rem auto;
}

.public-express-booking-modal .modal-content {
  height: 100%;
}

.public-express-booking-modal .modal-body {
  flex: 1 1 auto;
  overflow: hidden;
}

.public-express-booking-frame {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 75vh;
  border: 0;
  background: #fff;
}

@media (max-width: 576px) {
  .public-bookings-container {
    padding: 0 16px;
  }

  .public-bookings-hero {
    margin-bottom: 24px;
  }

  .public-bookings-hero .sales-page-profile__logo {
    width: 5.5rem;
    height: 5.5rem;
    margin-top: -2.75rem;
  }

  .public-bookings-page--embedded .public-bookings-grid {
    grid-template-columns: 1fr;
  }

  .public-bookings-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .public-bookings-section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
